import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Alert, Button, TextField } from '../../components/ui'
import { keys } from '../../api/keys'
import { replaceAvailability } from '../../api/objects-write'
import type { AvailabilityWindow } from '../../api/objects'
import { useSession } from '../../auth/session'
import { t } from '../../i18n'

/**
 * When a flat is free, entered by hand.
 *
 * The endpoint existed from the start and no screen ever offered it, so every
 * flat entered by hand carried "nobody has recorded when it is free" for ever —
 * and the matcher, correctly, will not promise a date nobody wrote down. A flat
 * with no window is a flat that fails the availability rule against any client
 * who named a move-in date, which is most of them.
 *
 * One window at a time. Student lets are sometimes free in two separate periods
 * and the schema carries several, but a second row is a rarity and a form that
 * leads with it is a form nobody finishes.
 */
export function AvailabilityForm({
  listingID,
  propertyID,
  windows,
}: {
  readonly listingID: string
  readonly propertyID: string
  readonly windows: readonly AvailabilityWindow[]
}) {
  const { can } = useSession()
  const client = useQueryClient()
  const [open, setOpen] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  const existing = windows[0]
  const [from, setFrom] = useState(day(existing?.from))
  const [to, setTo] = useState(day(existing?.to))

  function reset() {
    setFrom(day(existing?.from))
    setTo(day(existing?.to))
    setFailure(null)
  }

  const save = useMutation({
    mutationFn: () =>
      replaceAvailability(
        listingID,
        // An empty first day clears the window rather than writing a blank one:
        // "нет записи" and "свободна с неизвестно когда" are the same fact and
        // only one of them is storable.
        from.trim() === '' ? [] : [{ from: from.trim(), ...(to.trim() === '' ? {} : { to: to.trim() }) }],
      ),
    onSuccess: () => {
      setOpen(false)
      setFailure(null)
      void client.invalidateQueries({ queryKey: keys.property(propertyID) })
    },
    onError: (error: unknown) => {
      setFailure(error instanceof Error ? error.message : t('build.failed'))
    },
  })

  if (!can('listings.write')) return null

  if (!open) {
    return (
      <Button
        variant="ghost"
        onClick={() => {
          reset()
          setOpen(true)
        }}
      >
        {windows.length === 0 ? t('listing.availabilitySet') : t('listing.availabilityEdit')}
      </Button>
    )
  }

  return (
    <form
      className="build-form"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <div className="build-form__row">
        <TextField
          label={t('listing.availabilityFrom')}
          value={from}
          placeholder="2026-10-01"
          onChange={(event) => {
            setFrom(event.target.value)
          }}
        />
        <TextField
          label={t('listing.availabilityTo')}
          value={to}
          placeholder={t('listing.availabilityToHint')}
          onChange={(event) => {
            setTo(event.target.value)
          }}
        />
      </div>

      {failure === null ? null : <Alert tone="error">{failure}</Alert>}

      <div className="build-form__actions">
        <Button type="submit" busy={save.isPending}>
          {save.isPending ? t('build.saving') : t('build.save')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            reset()
            setOpen(false)
          }}
          disabled={save.isPending}
        >
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}

/** The day out of an RFC3339 timestamp, because the field asks for a day and
 * sending a timestamp back is how the channel form used to delete its own date. */
function day(iso: string | undefined): string {
  if (iso === undefined) return ''
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(iso)
  return match?.[1] ?? ''
}
